import { fileDownload } from 'utils/fileDownload';
import { logError } from 'utils/sentryUtils';

import { mmFetch } from './mmFetch';
import { mmFetchWithResponse } from './mmFetchWithResponse';

const logisticsApi = {
    confirmFBMZipUpload: async (key) => {
        const url = 'api/logistics/v1/fbm/zipMap/confirm';
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify({ key }),
        });
        return response;
    },
    getEasypostCarrierAccounts: async () => {
        const url = 'api/logistics/v1/easypostAccounts';
        return mmFetchWithResponse(url);
    },
    getLineHauls: async (status) => {
        const url = `api/logistics/v1/lineHauls?status=${status}`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getLineHaulShifts: async (week, year) => {
        let url = `api/logistics/v1/lineHauls/shifts`;
        if (week && year) {
            url += `?week=${week}&year=${year}`;
        }
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getRecentLineHaulShippingDays: async (facilityID, excludeCarrierShip = false) => {
        const url = `api/logistics/v1/lineHauls/getRecentShippingDays/${facilityID}?excludeCarrierShip=${excludeCarrierShip}`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getZipMapResults: async () => {
        const url = 'api/logistics/v1/zipMapUploads';
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getSubsOnInvalidLineHauls: async () => {
        const url = `api/logistics/v1/zipMapReassign/invalidLineHauls`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getZipMapReassignmentResults: async () => {
        const url = 'api/logistics/v1/zipMapReassign/results';
        const response = await mmFetchWithResponse(url);
        return response;
    },
    reassignSubsOnInvalidLineHauls: async (day) => {
        const url = `api/logistics/v1/zipMapReassign?day=${day}`;
        const response = await mmFetchWithResponse(url, {
            method: 'PUT',
        });
        return response;
    },
    resetLineHaulRouting: async (lineHaulID) => {
        const url = `api/logistics/v1/lineHauls/${lineHaulID}/resetRouting`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
        });
        return response;
    },
    resyncLineHaul: async (lineHaulID) => {
        const url = `api/logistics/v1/lineHauls/${lineHaulID}/resync`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
        });
        return response;
    },
    updateEasypostAccount: async (id, item) => {
        const url = `api/logistics/v1/easypostAccounts/${id}`;
        return mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify(item),
        });
    },
    updateCrossDock: async (id, item) => {
        const url = `api/logistics/v1/dbiCrossDocks/${id}`;
        const response = await mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify(item),
        });
        return response;
    },
    updateLineHaul: async (id, item) => {
        const url = `api/logistics/v1/lineHauls/${id}`;
        const response = await mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify(item),
        });
        return response;
    },
    updateLineHaulCarrier: async (id, item) => {
        const url = `api/logistics/v1/lineHauls/${id}/carrier`;
        return mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify(item),
        });
    },
    reassignPurchasedShipments: async ({
        fromID,
        includeCustom,
        includeMystery,
        quantity,
        readOnly,
        restrictZips,
        restrictZipsFile,
        toID,
    }) => {
        const formData = new FormData();

        formData.append('fromID', fromID);
        formData.append('toID', toID);
        formData.append('quantity', quantity);
        formData.append('restrictZips', restrictZips);
        formData.append('includeMystery', includeMystery);
        formData.append('includeCustom', includeCustom);
        formData.append('readOnly', readOnly);
        if (restrictZips === true) {
            formData.append('restrictZipsFile', restrictZipsFile);
        }

        const url = 'api/logistics/v1/transfer';
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: formData,
            processData: false,
            headers: {},
        });
        return response;
    },
    clearLineHaulShift: async (fromLineHaulID, sendParams) => {
        const url = `api/logistics/v1/lineHauls/${fromLineHaulID}/shifts/clear`;
        const response = await mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify(sendParams),
        });
        return response;
    },
    shiftLineHaul: async (fromLineHaulID, sendParams) => {
        const url = `api/logistics/v1/lineHauls/${fromLineHaulID}/shift`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(sendParams),
        });
        return response;
    },
    submitTransitDayFile: async ({ transitDayChanges }) => {
        const formData = new FormData();
        formData.append('transitDayChanges', transitDayChanges);

        const url = 'api/logistics/v1/transitDays';
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: formData,
            processData: false,
            headers: {},
        });
        return response;
    },
    submitZipFiles: async ({ backupFile, deliveryDay, fbmPartnerName, notes, optimalFile }) => {
        const formData = new FormData();
        formData.append('allZips', backupFile);
        formData.append('optimalZips', optimalFile);
        formData.append('day', deliveryDay);
        formData.append('notes', notes);
        formData.append('fbmPartnerName', fbmPartnerName);

        const url = 'api/logistics/v1/zipMap';
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: formData,
            processData: false,
            headers: {},
        });
        return response;
    },
    syncEasypostAccounts: async () => {
        const url = `api/logistics/v1/easypostAccounts/sync`;
        return mmFetchWithResponse(url, { method: 'POST' });
    },
    createLineHaul: async (linehaul) => {
        const url = `api/logistics/v1/lineHauls`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(linehaul),
        });
        return response;
    },
    getValidDaysForUpload: async () => {
        const url = `api/logistics/v1/zipMapUploads/days`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getReassignmentFile: async (id, type) => {
        const url = `api/logistics/v1/zipMapReassign/${id}/csv?type=${type}`;
        const filename = `zip_map_reassign_${id}_${type}.csv`;
        try {
            const res = await mmFetch(url);
            if (res.ok === true) {
                await fileDownload(res, filename);
            }
        } catch (e) {
            logError(e);
        }
    },
    getZipMapFile: async (uploadID, fileType, uploadDate, deliveryDay) => {
        const url = `api/logistics/v1/zipMapUploads/${uploadID}/file?type=${fileType}`;
        const filename = `zip_map_upload_${uploadID}-${deliveryDay}-${uploadDate}-${fileType}.csv`;
        try {
            const res = await mmFetch(url);
            if (res.ok === true) {
                await fileDownload(res, filename);
            }
        } catch (e) {
            logError(e);
        }
    },
    getLineHaulCarriers: async () => {
        const url = 'api/logistics/v1/carriers';
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getDBICrossDocks: async () => {
        const url = 'api/logistics/v1/dbiCrossDocks';
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getInboundCrossDocks: async () => {
        const url = 'api/logistics/v1/inboundCrossDocks';
        const response = await mmFetchWithResponse(url);
        return response;
    },
    updateInboundCrossDocks: async (id, item) => {
        const url = `api/logistics/v1/inboundCrossDocks/${id}`;
        const response = await mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify(item),
        });
        return response;
    },
    createInboundCrossDocks: async (crossDock) => {
        const url = `api/logistics/v1/inboundCrossDocks`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(crossDock),
        });
        return response;
    },
    getCurrentShippingRates: async () => {
        const url = 'api/logistics/v1/rates';
        const response = await mmFetchWithResponse(url);
        return response;
    },
    uploadFBMZipMap: async (data) => {
        const formData = new FormData();
        formData.append('itemFile', data.file);

        const url = 'api/logistics/v1/fbm/zipMap/upload';
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: formData,
            processData: false,
            headers: {},
        });
        return response;
    },
    uploadShippingRates: async (data) => {
        const formData = new FormData();
        formData.append('shippingRateFile', data.file);

        const url = 'api/logistics/v1/rates/upload';
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: formData,
            processData: false,
            headers: {},
        });
        return response;
    },
    confirmShippingRates: async (key) => {
        const url = 'api/logistics/v1/rates/confirm';
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify({ key }),
        });
        return response;
    },
    getCartExperiences: async () => {
        const url = 'api/logistics/v1/cartExperiences';
        const response = await mmFetchWithResponse(url);
        return response;
    },
    uploadCartExperience: async (data) => {
        const formData = new FormData();
        Object.entries(data).forEach(([key, value]) => formData.append(key, value));

        const url = 'api/logistics/v1/cartExperience';
        return mmFetchWithResponse(url, {
            method: 'POST',
            body: formData,
            processData: false,
            headers: {},
        });
    },
};

export { logisticsApi };
